import { AuthResponse, LoginForm, RegisterForm } from '../types/auth.types';
import config from '../config/env.config';
import i18n from '../i18n/i18n-setup';

interface ProtectedResponse {
  message: string;
  user: {
    id: string;
    username: string;
    stravaId?: string;
    mustChangePassword?: boolean;
  };
}

/** Error thrown by auth calls; `code` mirrors the backend's error code when it sent one. */
export interface AuthApiError extends Error {
    code?: string;
}

const toAuthApiError = async (response: Response, fallbackMessage: string): Promise<AuthApiError> => {
    const errorData = await response.json().catch(() => ({}));
    const error: AuthApiError = new Error(errorData.message || fallbackMessage);
    error.code = errorData.code;
    return error;
};

export const authService = {
    login: async (data: LoginForm): Promise<AuthResponse> => {
        const response = await fetch(`${config.API_URL}/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include', // Include httpOnly cookies
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw await toAuthApiError(response, 'Login failed');
        }

        const result = await response.json();
        
        // Tokens are now stored in httpOnly cookies, no localStorage needed
        return {
            token: '', // Empty since it's in httpOnly cookie
            refreshToken: '', // Empty since it's in httpOnly cookie
            user: result.user || {}
        };
    },

    // Exchanges a Google ID token for the app's httpOnly session cookies
    loginWithGoogle: async (credential: string): Promise<void> => {
        const response = await fetch(`${config.API_URL}/auth/google`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ credential }),
        });

        if (!response.ok) {
            throw await toAuthApiError(response, 'Google login failed');
        }
    },

    verifyEmail: async (token: string): Promise<void> => {
        const response = await fetch(`${config.API_URL}/verify-email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
        });

        if (!response.ok) {
            throw await toAuthApiError(response, 'Email verification failed');
        }
    },

    resendVerification: async (email: string): Promise<void> => {
        const response = await fetch(`${config.API_URL}/resend-verification`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            throw await toAuthApiError(response, 'Resending verification email failed');
        }
    },

    register: async (data: RegisterForm): Promise<void> => {
        const response = await fetch(`${config.API_URL}/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw new Error('Registration failed');
        }
    },

    connectStrava: async (): Promise<void> => {
        // Backend initiates OAuth flow and redirects
        window.location.href = `${config.API_URL}/connect/strava`;
    },

    validateToken: async (): Promise<ProtectedResponse | null> => {
        try {
            // Instead of using a dedicated /protected endpoint, we'll use /user/profile
            // which is a real endpoint that requires authentication
            const response = await fetch(`${config.API_URL}/user/profile`, {
                method: 'GET',
                credentials: 'include', // Include httpOnly cookies
            });

            if (!response.ok) {
                // Try to refresh token if validation fails
                const refreshSuccess = await authService.refreshToken();
                if (!refreshSuccess) {
                    return null;
                }
                // Retry validation after refresh
                const retryResponse = await fetch(`${config.API_URL}/user/profile`, {
                    method: 'GET',
                    credentials: 'include',
                });
                
                if (!retryResponse.ok) {
                    return null;
                }
                
                const userData = await retryResponse.json();
                return {
                    message: 'Token is valid',
                    user: {
                        id: userData._id || userData.id,
                        username: userData.username || '',
                        stravaId: userData.stravaId,
                        mustChangePassword: userData.mustChangePassword
                    }
                };
            }

            const userData = await response.json();
            return {
                message: 'Token is valid',
                user: {
                    id: userData._id || userData.id,
                    username: userData.username || '',
                    stravaId: userData.stravaId,
                    mustChangePassword: userData.mustChangePassword
                }
            };
        } catch (error) {
            console.error('Error validating token:', error);
            return null;
        }
    },
    
    // Method to just check authentication status
    isAuthenticated: async (): Promise<boolean> => {
        // Since tokens are in httpOnly cookies, we need to make an API call to check
        const result = await authService.validateToken();
        return result !== null;
    },
    
    // Method to refresh the token
    refreshToken: async (): Promise<boolean> => {
        try {
            const response = await fetch(`${config.API_URL}/refresh-token`, {
                method: 'POST',
                credentials: 'include', // Include httpOnly cookies
            });

            return response.ok;
        } catch (error) {
            console.error('Error refreshing token:', error);
            return false;
        }
    },

    // Method to logout user
    logout: async (): Promise<void> => {
        try {
            // Call backend logout to clear httpOnly cookies
            await fetch(`${config.API_URL}/logout`, {
                method: 'POST',
                credentials: 'include',
            });
        } catch (error) {
            console.error('Error during logout:', error);
        }
        
        // Redirect to login page
        window.location.href = '/login';
    },

    // Method to request password reset
    forgotPassword: async (data: { email: string }): Promise<void> => {
        const response = await fetch(`${config.API_URL}/forgot-password`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || i18n.t('auth:forgot.serviceFailed'));
        }
    },

    // Method to change password
    changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
        const response = await fetch(`${config.API_URL}/change-password`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || i18n.t('auth:changePassword.serviceFailed'));
        }
    }
};